import { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import NodeChartTooltip from './NodeChartTooltip';
import {
  AXIS,
  COMPACT_CATEGORY_TICK,
  createValueLabelRenderer,
  formatValueLabel,
  resolveAxisWidth,
  resolveCodeColor
} from './nodeChartTheme';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { trackEvent } from '../../lib/usageEvents';

const renderLabel = createValueLabelRenderer({ orientation: 'horizontal' });

/**
 * Горизонтальный рейтинг категорий. Чистый рендерер: данные уже отсортированы и обрезаны
 * (`applyValueShaping`), шапку и состояния владеет `NodeChartCard`.
 *
 * На телефоне колонка подписей зажимается долей ширины графика, а подпись значения
 * сокращается до числа: иначе ось и отступ под «N (x%)» вместе были шире самого графика.
 */
const NodeBarChart = ({ spec, rows, height, onSelect }) => {
  const isMobile = useIsMobile();
  const [chartWidth, setChartWidth] = useState(0);

  const axisWidth = useMemo(
    () => resolveAxisWidth(rows, { compact: isMobile, containerWidth: chartWidth }),
    [rows, isMobile, chartWidth]
  );

  // Подпись кладём в данные готовой строкой: `LabelList` отдаёт в content только
  // `value`, поэтому собрать «N (x%)» из calls и share на месте отрисовки нельзя.
  const data = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        valueLabel: formatValueLabel(row.calls, row.share, { compact: isMobile })
      })),
    [rows, isMobile]
  );

  const colors = useMemo(() => {
    // Набор занятых слотов палитры общий на карточку, иначе два кода с одинаковым
    // хешем встали бы рядом одним цветом.
    const usedColors = new Set();
    return rows.map((row, index) =>
      resolveCodeColor(row.code, index, { tone: spec.tone, colorRoles: spec.colorRoles, usedColors })
    );
  }, [rows, spec.tone, spec.colorRoles]);

  return (
    <ResponsiveContainer
      width="100%"
      height={height}
      minWidth={0}
      minHeight={160}
      onResize={(width) => setChartWidth(width)}
    >
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 8, right: isMobile ? AXIS.labelGutterCompact : AXIS.labelGutter, left: isMobile ? 0 : 8, bottom: 8 }}
      >
        <CartesianGrid {...AXIS.grid} horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={AXIS.tick} />
        <YAxis
          type="category"
          dataKey="label"
          width={axisWidth}
          interval={0}
          tick={isMobile ? COMPACT_CATEGORY_TICK : AXIS.tick}
        />
        <Tooltip content={<NodeChartTooltip />} wrapperStyle={AXIS.tooltipWrapperStyle} cursor={{ fillOpacity: 0.1 }} />
        <Bar
          dataKey="calls"
          radius={AXIS.radiusHorizontal}
          maxBarSize={AXIS.maxBarSize}
          isAnimationActive={false}
          cursor={onSelect ? 'pointer' : 'default'}
          onClick={(entry) => {
            if (onSelect) {
              trackEvent('nodes.bar.select');
            }
            onSelect?.(entry?.payload);
          }}
        >
          {data.map((row, index) => (
            <Cell key={row.code} fill={colors[index]} />
          ))}
          <LabelList dataKey="valueLabel" content={renderLabel} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default NodeBarChart;
